import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { activatedHelp } from '../funnel/funnel-words';
import { beforeWords, compare } from '../period/compare';
import { PeriodChart } from '../period/PeriodChart';
import { periodLabel, tileLabel, type Period, type PeriodChoice } from '../period/period';
import { TodayHero } from './TodayHero';
import { TrendPanel } from './TrendPanel';
import { todayPages, withPeriod } from './today-links';
import type { DailyMetric, Today, TodayMetric } from './today-types';
import '../admin.css';
import './today.css';

interface Panel {
  metric: TodayMetric;
  title: string;
  /** What the number means, when a word in it has one definition (activated). */
  help?: string;
  goodWhen: 'up' | 'down';
}

/** The six questions, in the order a company moves: it signs up, activates, pays, reads, exports, struggles. */
export const todayPanels: readonly Panel[] = [
  { metric: 'signups', title: 'Sign-ups', goodWhen: 'up' },
  { metric: 'activated', title: 'Activated', help: activatedHelp, goodWhen: 'up' },
  {
    metric: 'paying',
    title: 'New paying',
    help: 'Companies whose first card money ever came in the period.',
    goodWhen: 'up',
  },
  { metric: 'pages', title: 'Pages read', goodWhen: 'up' },
  { metric: 'exports', title: 'Exports', goodWhen: 'up' },
  {
    metric: 'problems',
    title: 'Problems',
    help: 'Refusals, errors, failed scans, uploads and exports that people hit.',
    goodWhen: 'down',
  },
];

export interface TodayBoardProps {
  today: Today;
  /** The period control's choice: it words every label and rides on every link. */
  choice: PeriodChoice;
  /** The router's link, so every number opens the page behind it. */
  renderLink: (href: string, children: ReactNode) => ReactNode;
  /** A bucket of the chart was chosen: show those days only. */
  onPick?: ((bucket: Period) => void) | undefined;
}

/** 12,345 as "12.3k" once a number is too long for a panel. */
function short(f: Formatter, n: number): string {
  return n >= 10_000 ? `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : f.int(n);
}

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** Pages read over the period as bars, the period before as the faint line. */
function PagesChart({ today, choice, onPick }: Omit<TodayBoardProps, 'renderLink'>): ReactNode {
  const f = useFormat();
  const pages = today.metrics.find((m) => m.metric === 'pages');
  const title = tileLabel('Pages read', choice, today.period);
  return (
    <Card className="ad-today__chart">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <PeriodChart
        label={`${title}, by ${today.bucket}`}
        buckets={today.buckets}
        series={[
          {
            key: 'pages',
            label: 'Pages read',
            values: pages?.series ?? [],
            kind: 'bars',
            format: f.int,
          },
        ]}
        previous={{
          label: capital(beforeWords(today.period.days)),
          values: pages?.previousSeries ?? [],
        }}
        onPick={onPick}
        height={240}
        empty={`No pages were read, ${periodLabel(today.period)}.`}
      />
    </Card>
  );
}

function panelOf(panel: Panel, metric: DailyMetric, props: TodayBoardProps, f: Formatter) {
  return (
    <TrendPanel
      label={tileLabel(panel.title, props.choice, props.today.period)}
      total={short(f, metric.total)}
      compared={compare(metric.total, metric.previous, {
        days: props.today.period.days,
        format: f.int,
        goodWhen: panel.goodWhen,
      })}
      current={metric.series}
      previous={metric.previousSeries}
      help={panel.help}
    />
  );
}

/**
 * What changed: the money collected, large; pages read over the period against the one before; then
 * six small trends, each a link to the page behind it on the same days.
 */
export function TodayBoard(props: TodayBoardProps): ReactNode {
  const { today, choice, renderLink } = props;
  const f = useFormat();
  const byMetric = new Map(today.metrics.map((m) => [m.metric, m]));
  return (
    <div className="ad-today">
      {renderLink(
        withPeriod('/admin/revenue', choice),
        <TodayHero today={today} choice={choice} />,
      )}
      <PagesChart today={today} choice={choice} onPick={props.onPick} />
      <ul className="ad-today__panels">
        {todayPanels.map((panel) => {
          const metric = byMetric.get(panel.metric);
          return metric ? (
            <li key={panel.metric}>
              {renderLink(
                withPeriod(todayPages[panel.metric], choice),
                panelOf(panel, metric, props, f),
              )}
            </li>
          ) : null;
        })}
      </ul>
    </div>
  );
}
